'use client';

import { lazy, Suspense, type ComponentProps, type ComponentType } from 'react';
import { DialogContent, DialogTitle } from '@eliadmin/core/src/components/ui/dialog';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

/*
 * Core file with one DLZ change: the „Pridané do košíka“ side panel must not be preceded by the core
 * centred loading card. Its chunk is preloaded when the browser is idle and the boundary renders
 * nothing while it resolves (the panel shows its own skeleton until the cart is refetched).
 */
const loadConfirmation = () =>
  import('@eliadmin/core/src/components/product/dialog/add-to-cart-dialog');
if (typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) window.requestIdleCallback(() => void loadConfirmation());
  else setTimeout(() => void loadConfirmation(), 2000);
}

// All actual implementations still resolve project overrides. Only their loading boundary moves.
const SignIn = lazy(() =>
  import('@eliadmin/core/src/components/auth/sign-in-dialog').then(module => ({
    default: module.SignInDialog,
  })),
);
const Confirmation = lazy(() =>
  loadConfirmation().then(module => ({ default: module.AddToCartDialog })),
);
const Variants = lazy(() =>
  import('@eliadmin/core/src/components/product/dialog/add-to-cart-variant-dialog').then(
    module => ({ default: module.AddToCartVariantDialog }),
  ),
);

function DialogLoading({
  signIn = false,
  showSpinner = false,
}: {
  signIn?: boolean;
  showSpinner?: boolean;
}) {
  const { t } = useTranslations();
  return (
    <DialogContent aria-busy="true" className="max-h-[80dvh] min-h-48 max-w-[652px]">
      <DialogTitle>{t(signIn ? 'text190' : 'text227')}</DialogTitle>
      {showSpinner ? (
        <div aria-hidden="true" className="flex h-24 items-center justify-center">
          <Spinner className="h-8 w-8 text-primary" />
        </div>
      ) : (
        <Skeleton className="h-24 w-full" />
      )}
    </DialogContent>
  );
}
// Legacy no-argument dialogs accept/ignore contextual props (inCart, stayOnCurrentPage).
// Typed project dialogs retain their actual prop contract instead of a global whitelist.
type SignInProps = { inCart?: boolean } & (ComponentProps<typeof SignIn> extends object
  ? ComponentProps<typeof SignIn>
  : Record<string, unknown>);
const SignInImplementation = SignIn as ComponentType<SignInProps>;
export function SignInDialog(props: SignInProps) {
  return (
    <Suspense fallback={<DialogLoading signIn />}>
      <SignInImplementation {...props} />
    </Suspense>
  );
}
export function AddToCartDialog(props: ComponentProps<typeof Confirmation>) {
  return (
    <Suspense fallback={null}>
      <Confirmation {...props} />
    </Suspense>
  );
}
export function AddToCartVariantDialog(props: ComponentProps<typeof Variants>) {
  return (
    <Suspense fallback={<DialogLoading showSpinner />}>
      <Variants {...props} />
    </Suspense>
  );
}

export const SIGN_IN_DIALOG = 'SIGN_IN_DIALOG';
export const ADD_TO_CART_DIALOG = 'ADD_TO_CART_DIALOG';
export const ADD_TO_CART_VARIANT_DIALOG = 'ADD_TO_CART_VARIANT_DIALOG';
SignInDialog.id = SIGN_IN_DIALOG;
AddToCartDialog.id = ADD_TO_CART_DIALOG;
AddToCartVariantDialog.id = ADD_TO_CART_VARIANT_DIALOG;
